import { useEffect, useState, type ReactNode } from 'react';
import { Check, Copy, Loader2, RefreshCw } from 'lucide-react';

import type { TtqSession } from '../../services/ttqService';
import { ttqService } from '../../services/ttqService';
import { SUBJECT_CONFIG } from '../../utils/subjectConfig';
import {
  computeTtqKnowledgeEntropy,
  formatTtqSessionProgress,
  resolveTtqSessionTitle,
  ttqSessionStatusLabel,
} from '../../utils/ttqDisplay';

type TtqSessionDetailDialogProps = {
  open: boolean;
  session: TtqSession;
  onClose: () => void;
  onSessionUpdated?: (session: TtqSession) => void;
};

type DetailRowProps = {
  label: string;
  value: ReactNode;
  hint?: string;
};

function DetailRow({ label, value, hint }: DetailRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-sm text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">
        <div>{value}</div>
        {hint ? <p className="mt-0.5 text-xs text-muted">{hint}</p> : null}
      </dd>
    </div>
  );
}

export function TtqSessionDetailDialog({
  open,
  session,
  onClose,
  onSessionUpdated,
}: TtqSessionDetailDialogProps) {
  const [copied, setCopied] = useState(false);
  const [refreshingTitle, setRefreshingTitle] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [titleMessage, setTitleMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setRefreshingTitle(false);
      setTitleError(null);
      setTitleMessage(null);
    }
  }, [open]);

  if (!open) return null;

  const progress = formatTtqSessionProgress(session.particles ?? []);
  const entropy = computeTtqKnowledgeEntropy(session.particles ?? []);
  const subjectLabel = SUBJECT_CONFIG[session.subjectCode]?.label ?? session.subjectCode;
  const correlationScore = typeof session.correlationAnalysis?.score === 'number'
    ? session.correlationAnalysis.score
    : null;
  const displayTitle = resolveTtqSessionTitle(session);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(session.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const handleRefreshTitle = async () => {
    if (refreshingTitle) return;
    setRefreshingTitle(true);
    setTitleError(null);
    setTitleMessage(null);
    try {
      const result = await ttqService.ensureSessionTitle(session.id, { force: true });
      onSessionUpdated?.(result.session);
      setTitleMessage(result.message);
    } catch (error) {
      setTitleError(error instanceof Error ? error.message : '刷新标题失败');
    } finally {
      setRefreshingTitle(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-xl border border-border bg-surface p-4 shadow-xl sm:rounded-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-ink">品题详情</h3>
        <div className="mt-1 flex items-start gap-2">
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-muted">{displayTitle}</p>
          <button
            type="button"
            disabled={refreshingTitle}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:bg-black/[0.04] disabled:opacity-50"
            title="根据颗粒与聚合结果重写总结性标题"
            onClick={() => { void handleRefreshTitle(); }}
          >
            {refreshingTitle ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            刷新标题
          </button>
        </div>
        {titleError ? (
          <p className="mt-1 text-[11px] text-danger">{titleError}</p>
        ) : titleMessage ? (
          <p className="mt-1 text-[11px] text-muted">{titleMessage}</p>
        ) : null}

        <dl className="mt-4 divide-y divide-border">
          <DetailRow
            label="品题 ID"
            value={(
              <div className="flex flex-col items-end gap-2">
                <code className="w-full break-all rounded bg-black/[0.04] px-1.5 py-0.5 text-left text-xs leading-relaxed">
                  {session.id}
                </code>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted hover:bg-black/[0.04]"
                  onClick={() => { void handleCopyId(); }}
                >
                  {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                  {copied ? '已复制' : '复制'}
                </button>
              </div>
            )}
          />
          <DetailRow label="学科" value={subjectLabel} />
          <DetailRow label="状态" value={ttqSessionStatusLabel(session.status)} />
          <DetailRow label="包含颗粒数" value={`${progress.total} 个`} />
          <DetailRow
            label="分析进度"
            value={`${progress.done} 已完成 · ${progress.running} 分析中 · ${progress.failed} 需重试`}
          />
          <DetailRow
            label="知识熵"
            value={entropy.label}
            hint={entropy.hint}
          />
          {correlationScore !== null ? (
            <DetailRow
              label="共同能力关联度"
              value={`${Math.round(correlationScore * 100)}%`}
              hint="来自聚合分析结果"
            />
          ) : null}
          <DetailRow
            label="多颗粒聚合"
            value={session.aggregationAvailable ? '可用' : '不可用'}
          />
          {(session.cttqRecords?.length ?? 0) > 0 ? (
            <DetailRow label="CTTQ 记录" value={`${session.cttqRecords!.length} 条`} />
          ) : null}
          {session.summary ? (
            <DetailRow label="摘要" value={<span className="text-left">{session.summary}</span>} />
          ) : null}
        </dl>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted hover:bg-black/[0.03]"
            onClick={onClose}
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
